import { BARCODE_TYPES, cleanBarcode, isValidBarcode, type BarcodeDto, type ItemDetailDto, type ItemDto } from '@bnn/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { errMsg } from '../lib/useApi';
import { fetchItemDetail, ItemPicker, UomOptions } from './ItemPicker';
import { Alert, Button, Field, Input, Select, Spinner } from './ui';

/**
 * Bind a barcode to item + UoM (D3: usable immediately). Used from the scan screen (unknown barcode,
 * or "บันทึก Barcode" for an item without one) and from the Barcode page.
 */
export function BarcodeBindForm({
  barcode: initialBarcode = '',
  item: initialItem,
  uom: initialUom,
  sessionId,
  onDone,
  onCancel,
}: {
  barcode?: string;
  item?: ItemDetailDto;
  uom?: string;
  sessionId?: string;
  onDone: (b: BarcodeDto, item: ItemDetailDto) => void;
  onCancel?: () => void;
}) {
  const [barcode, setBarcode] = useState(initialBarcode);
  const [item, setItem] = useState<ItemDetailDto | null>(initialItem ?? null);
  const [uom, setUom] = useState(initialUom ?? initialItem?.inventory_uom ?? '');
  const [type, setType] = useState<(typeof BARCODE_TYPES)[number]>('FACTORY');
  const [supplier, setSupplier] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item && !item.uoms.some((u) => u.uom_code === uom)) setUom(item.inventory_uom);
  }, [item, uom]);

  const pick = async (i: ItemDto) => {
    setLoadingItem(true);
    setError('');
    try {
      const d = await fetchItemDetail(i.item_code);
      setItem(d);
      setUom(d.inventory_uom);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoadingItem(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const code = cleanBarcode(barcode);
    if (!isValidBarcode(code)) return setError('Barcode ไม่ถูกต้อง (ตัวอักษรอังกฤษ/ตัวเลข 1–64 ตัว)');
    if (!item) return setError('กรุณาเลือกสินค้า');
    setBusy(true);
    setError('');
    try {
      const b = await api.post<BarcodeDto>('/barcodes', {
        barcode: code,
        item_code: item.item_code,
        uom_code: uom,
        barcode_type: type,
        supplier: supplier || null,
        ...(sessionId && { session_id: sessionId }),
      });
      onDone(b, item);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'NETWORK' ? 'บันทึก Barcode ต้องเชื่อมต่อเน็ต' : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Alert>{error}</Alert>}
      <Field label="Barcode">
        <Input value={barcode} onChange={(e) => setBarcode(e.target.value)} readOnly={!!initialBarcode} placeholder="สแกนหรือพิมพ์ Barcode" autoFocus={!initialBarcode} className="font-mono" />
      </Field>
      {item ? (
        <div className="rounded-lg bg-chip px-3 py-2">
          <div className="flex items-start gap-2">
            <div className="mr-auto">
              <b>{item.item_code}</b> {item.item_name}
              <div className="text-xs text-muted">Inventory UoM: {item.inventory_uom}</div>
            </div>
            {!initialItem && (
              <Button size="sm" type="button" onClick={() => setItem(null)}>
                เปลี่ยน
              </Button>
            )}
          </div>
        </div>
      ) : (
        <Field label="สินค้า">{loadingItem ? <Spinner /> : <ItemPicker onPick={(i) => void pick(i)} autoFocus={!!initialBarcode} />}</Field>
      )}
      {item && (
        <>
          <Field label="หน่วยของ Barcode นี้ (สแกน 1 ครั้ง = 1 หน่วยนี้)" hint="ตรวจให้ตรงกับบรรจุภัณฑ์ที่ติด Barcode">
            <Select value={uom} onChange={(e) => setUom(e.target.value)}>
              <UomOptions item={item} />
            </Select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="ประเภท">
              <Select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                <option value="FACTORY">FACTORY (จากผู้ผลิต)</option>
                <option value="INTERNAL">INTERNAL (ภายใน)</option>
              </Select>
            </Field>
            <Field label="Supplier (ไม่บังคับ)">
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={150} />
            </Field>
          </div>
        </>
      )}
      <div className="flex gap-2">
        {onCancel && (
          <Button type="button" onClick={onCancel}>
            ยกเลิก
          </Button>
        )}
        <Button type="submit" variant="primary" className="grow" loading={busy} disabled={!item}>
          บันทึก Barcode
        </Button>
      </div>
    </form>
  );
}
